import { useFileOrganizer } from "@via/hooks/useFileOrganizer.tsx";
import { type IImportedFile } from "@via/pages/data/file-organizer/file-organizer.tsx";
import {
  getDirectoryPath,
  getFileNameWithoutPath,
  getMergedDirectoryPath,
  getNameWithPath,
  getSampleName,
  groupFilesByPattern,
} from "@via/pages/data/file-organizer/file-patterns/helpers.tsx";
import { type JSX, useEffect, useState } from "react";
import { AutoMergePatternInput } from "@via/pages/data/file-organizer/file-patterns/auto-merge-pattern-input.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { Sample } from "@via/context/file-organizer-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { DataSourceProvider } from "common";

export default function SingleList({
  fileList,
  globalBucketName,
  toBeRemovedFileIds,
  provider,
}: {
  fileList: IImportedFile[];
  globalBucketName: string;
  toBeRemovedFileIds: string[];
  provider: DataSourceProvider;
}): JSX.Element {
  const { setNotification, NOTIFICATION } = useNotifications();
  const { addNewSamples, resetSampleList, resetTrigger } = useFileOrganizer();

  const [filteredFileList, setFilteredFileList] = useState<IImportedFile[]>([]);

  useEffect(() => {
    const filtered = [...fileList];
    setFilteredFileList(filtered);
  }, [fileList]);

  useEffect(() => {
    setFilteredFileList(prevList =>
      prevList.map(file => {
        if (toBeRemovedFileIds.includes(file.id || "")) {
          return {
            ...file,
            isSampled: false,
            isChecked: false,
          };
        } else {
          return file;
        }
      })
    );
  }, [toBeRemovedFileIds]);

  const handleAutoMergeClick = (autoMergePattern: string) => {
    const sampleList = filteredFileList.filter(file => !file.isSampled);

    try {
      const groupedFiles = groupFilesByPattern(sampleList, autoMergePattern);

      const toBeAddedSamples: Sample[] = [];
      Object.entries(groupedFiles).forEach(([groupKey, files]) => {
        const mergedSample = {
          id: "",
          name: groupKey,
          filesUsed: "",
          directory: "",
          file1: "",
          "Provider IDs": "",
        };

        files.forEach((file, index) => {
          file.isSampled = true;

          if (index > 0) {
            mergedSample.id += ",";
            mergedSample.filesUsed += " | ";
            mergedSample.directory += ", ";
            mergedSample.file1 += ",";
          }

          mergedSample.id += file.id;
          mergedSample.filesUsed += getFileNameWithoutPath(file.name);
          mergedSample.directory += file.name;
          mergedSample.file1 += getNameWithPath(
            file.path || "",
            file.displayName || "",
            file.name || "",
            provider
          );
          mergedSample["Provider IDs"] +=
            provider === DataSourceProvider.BaseSpace ? file.id : "";
        });

        mergedSample.directory = getMergedDirectoryPath(
          mergedSample.directory,
          globalBucketName
        );
        toBeAddedSamples.push(mergedSample);
      });
      addNewSamples(toBeAddedSamples);
    } catch (error) {
      setNotification({
        id: "single-auto-merge-failed",
        message: "Auto Merge Failed",
        details: "Invalid Pattern",
        variant: NOTIFICATION.ERROR,
      });
    }
  };

  const handleFilteredFileClick = (clickedFile: IImportedFile): void => {
    const updatedList = filteredFileList.map(file =>
      file.name === clickedFile.name
        ? { ...file, isChecked: !file.isChecked }
        : file
    );

    setFilteredFileList(updatedList);
  };

  const handleResetClick = (): void => {
    resetSampleList();
    filteredFileList.forEach(file => {
      file.isSampled = false;
      file.isChecked = false;
    });
  };

  // Watch for reset trigger from context (e.g., when vmetaProjectId changes)
  useEffect(() => {
    if (resetTrigger > 0) {
      handleResetClick();
    }
  }, [resetTrigger]);

  const handleMergeSelectedFilesClick = (): void => {
    const mergedSample = {
      id: "",
      name: "",
      filesUsed: "",
      directory: "",
      file1: "",
      "Provider IDs": "",
    };

    const selectedFiles = filteredFileList.filter(
      file => file.isChecked && !file.isSampled
    );
    if (selectedFiles && selectedFiles.length > 0) {
      mergedSample.name = getSampleName(
        getFileNameWithoutPath(selectedFiles[0].name),
        "."
      );

      selectedFiles.forEach((file, index) => {
        file.isSampled = true;

        if (index > 0) {
          mergedSample.id += ",";
          mergedSample.filesUsed += " | ";
          mergedSample.directory += ", ";
          mergedSample.file1 += ",";
          mergedSample["Provider IDs"] += ",";
        }

        mergedSample.id += file.id;
        mergedSample.filesUsed += getFileNameWithoutPath(file.name);
        mergedSample.directory += file.name;
        mergedSample.file1 += getNameWithPath(
          file.path || "",
          file.displayName || "",
          file.name || "",
          provider
        );
        mergedSample["Provider IDs"] +=
          provider === DataSourceProvider.BaseSpace ? file.id : "";
      });

      mergedSample.directory = getMergedDirectoryPath(
        mergedSample.directory,
        globalBucketName
      );

      addNewSamples([mergedSample]);
    }
  };

  const handleAddSelectedFilesClick = (): void => {
    const sampleList = filteredFileList
      .filter(file => file.isChecked && !file.isSampled)
      .map(file => {
        file.isSampled = true;

        return {
          id: file.id,
          name: getSampleName(getFileNameWithoutPath(file.name), "."),
          filesUsed: getFileNameWithoutPath(file.name),
          directory: getDirectoryPath(file.name, globalBucketName),
          file1: getNameWithPath(
            file.path || "",
            file.displayName || "",
            file.name || "",
            provider
          ),
          "Provider IDs":
            provider === DataSourceProvider.BaseSpace ? file.id : "",
        };
      });

    addNewSamples(sampleList);
  };

  const handleAddAllFilesClick = (): void => {
    const sampleList = filteredFileList
      .filter(file => !file.isSampled)
      .map(file => {
        file.isSampled = true;

        return {
          id: file.id,
          name: getSampleName(getFileNameWithoutPath(file.name), "."),
          filesUsed: getFileNameWithoutPath(file.name),
          directory: getDirectoryPath(file.name, globalBucketName),
          file1: getNameWithPath(
            file.path || "",
            file.displayName || "",
            file.name || "",
            provider
          ),
          "Provider IDs":
            provider === DataSourceProvider.BaseSpace ? file.id : "",
        };
      });

    addNewSamples(sampleList);
  };

  const buttonDetails = [
    {
      label: "Reset",
      onClick: handleResetClick,
      testId: "singleListResetButton",
    },
    {
      label: "Merge Selected Files",
      onClick: handleMergeSelectedFilesClick,
      testId: "singleListMergeButton",
    },
    {
      label: "Add Selected Files",
      onClick: handleAddSelectedFilesClick,
      testId: "singleListAddSelectedButton",
    },
    {
      label: "Add All Files",
      onClick: handleAddAllFilesClick,
      testId: "singleListAddAllButton",
    },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 gap-4">
        <div className="mt-8">
          <div
            data-testid="singleListDiv"
            className="mt-4 h-60 overflow-auto rounded-md border border-gray-200">
            {filteredFileList.map(file =>
              file.isSampled ? null : (
                <div
                  key={file.name}
                  className={classNames(
                    "flex cursor-pointer gap-3 px-2 py-2 hover:bg-gray-50 text-sm",
                    file.isChecked ? "bg-indigo-100 hover:bg-indigo-200" : ""
                  )}
                  onClick={() => {
                    handleFilteredFileClick(file);
                  }}>
                  {getFileNameWithoutPath(file.name)}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      <AutoMergePatternInput onAutoMergeClick={handleAutoMergeClick} />

      <div className="mt-5 flex justify-end gap-2">
        {buttonDetails.map((button, index) => (
          <button
            data-testid={button.testId}
            key={index}
            onClick={button.onClick}
            className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            {button.label}
          </button>
        ))}
      </div>
    </div>
  );
}
